// SCR-FRIENDS, MOD-INVITE, уважение и MOD-MORE (E10-T04, ux/10 §1–3, ux/03 §11). Друзья — GET /friends, рефералы —
// GET /referrals (награда за ступени начисляется сервером сама: «получено», без кнопки «Забрать»). Уважение —
// поштучно (кнопки «Уважить всех» нет, UXQ-06), состояние «уважил сегодня ✓» — с сервера (getPlayer.respectedToday)
// или после своего тапа до 04:00 МСК. Пределы (10 уважений в сутки, 100 друзей) — конфиг сервера `social`.
import { signal } from '@preact/signals';
import { useState } from 'preact/hooks';
import { reduced } from '../ui/motion.js';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { CLIENT_BUILD } from '../app/app.js';
import { MoreIcon, useMoreItems, type MoreItem } from './home-rail.js';
import { t as tr } from '../i18n/t.js';
import { amount } from '../i18n/format.js';
import { ApiCallError } from '../net/api-client.js';
import { clientConfig, online, player } from '../state/game.js';
import { storage } from '../state/storage.js';
import { versionAtLeast } from '../telegram/webapp.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, useActionBar } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { I } from '../assets/icons.js';
import { assetEntry } from '../assets/manifest.js';
import { Icon } from '../assets/ui.js';
import '../ui/store.css';

type Friends = ResponseOf<Api['getFriends']>;
type Referrals = ResponseOf<Api['getReferrals']>;
type Invited = Referrals['invited'][number];

// ---------------- уважение ----------------

/** Игровые сутки (сброс в 04:00 МСК = 01:00 UTC): ключ дня для «уважил сегодня». */
export function gameDay(ms: number): string {
  return new Date(ms - 3_600_000).toISOString().slice(0, 10);
}

const RESPECT_KEY = 'respected';
function readRespected(): Set<string> {
  const v = storage.getJson<{ day: string; ids: string[] }>(RESPECT_KEY);
  return new Set(v && v.day === gameDay(Date.now()) ? v.ids : []);
}
/** Кому я уже сказал «уважаю» в эти игровые сутки (с сервера и по своим тапам). */
export const respectedToday = signal<Set<string>>(readRespected());
export function markRespected(id: string): void {
  const s = new Set(readRespected());
  s.add(id);
  storage.setJson(RESPECT_KEY, { day: gameDay(Date.now()), ids: [...s] });
  respectedToday.value = s;
}

const RESPECT_ERR: Record<string, string> = {
  DAILY_LIMIT: 'CL-RESPECT-LIMIT', SELF: 'CL-RESPECT-SELF', REQUIREMENT_NOT_MET: 'CL-RESPECT-LOW', NOT_FOUND: 'CL-RESPECT-GONE',
};

/** «Уважить» одним тапом: рукопожатие 44 × 44; после — «уважил сегодня ✓» (ux/10 §3). */
export function useRespect() {
  const app = useApp();
  return async (publicId: string): Promise<boolean> => {
    try {
      const r = await app.call('respectPlayer', { params: { publicId }, body: {} });
      markRespected(publicId);
      app.haptic('success');
      showToast(tr('CL-RESPECT-DONE', { n: r.leftToday }), 'success');
      return true;
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code === 'ALREADY_RESPECTED_TODAY') { markRespected(publicId); return true; }
      app.haptic('error');
      if (RESPECT_ERR[code]) { showToast(tr(RESPECT_ERR[code]!), 'error'); return false; }
      app.handleError(e);
      return false;
    }
  };
}

export function RespectButton(p: { publicId: string; given?: boolean; testId?: string }) {
  const respect = useRespect();
  const [busy, setBusy] = useState(false);
  const [just, setJust] = useState(false);
  const done = p.given || respectedToday.value.has(p.publicId);
  // Только что уважил — отметка «прыгает» и от неё расходится кольцо; уже уважённые в списке стоят спокойно.
  if (done) return <span class={`respect-done small ${just && !reduced() ? 'just' : ''}`} data-testid={p.testId ? `${p.testId}-done` : undefined}>{tr('CL-RESPECT-GIVEN')}</span>;
  return (
    <button class="respect-btn" aria-label={tr('UI-BTN-RESPECT')} data-testid={p.testId} disabled={busy || !online.value}
      onClick={async (e) => { e.stopPropagation(); setBusy(true); setJust(await respect(p.publicId)); setBusy(false); }}>
      <I n="hand" size={22} />
    </button>
  );
}

// ---------------- приглашённые ----------------

/** Статус приглашённого (ux/10 §1): ждём 3-й уровень / получено / рамка «Наставник» / без награды (причину не раскрываем). */
export function invitedStatus(i: Pick<Invited, 'status' | 'level'>): { key: string; tone: 'muted' | 'pos' | 'neg'; progress?: number } {
  switch (i.status) {
    case 'qualified_l10': return { key: 'CL-REF-L10', tone: 'pos' };
    case 'qualified_l3': return { key: 'CL-REF-L3', tone: 'pos' };
    case 'capped': case 'rejected_fraud': return { key: 'CL-REF-NONE', tone: 'neg' };
    default: return { key: 'CL-REF-WAIT', tone: 'muted', progress: Math.min(i.level, 3) };
  }
}

/** Лимит друзей для строки «Друзей: N / 100» — из конфига `social`, иначе запасное значение показа (ГД 14 §2). */
export function friendsMax(cfg: Record<string, unknown>): number {
  const s = cfg['social'] as { friendsMax?: number } | undefined;
  return s?.friendsMax ?? 100;
}

function nameOf(i: { publicId: string; displayName?: string | undefined }): string {
  return i.displayName ?? tr('CL-REF-PLAYER', { id: i.publicId });
}

export function FriendsScreen(_p: ScreenProps) {
  const app = useApp();
  const fr = useLoad(() => app.call('getFriends'), { cached: storage.getJson<Friends>('friendsCache') ?? null, onData: (d) => storage.setJson('friendsCache', d) });
  const refs = useLoad(() => app.call('getReferrals'));
  const [busy, setBusy] = useState<string | null>(null);
  // «Позвать друга» — первой крупной кнопкой экрана (пункт «Позвать» колонки на главном объединён с «Друзьями»).
  useActionBar({ back: true });
  const cachedLink = storage.get('refLink');
  const link = refs.data?.link ?? cachedLink ?? null;
  if (refs.data?.link && refs.data.link !== cachedLink) storage.set('refLink', refs.data.link);
  const reward = refs.data?.reward && refs.data.rewardsLeft > 0 ? grantText(refs.data.reward) : '';
  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try { await fn(); showToast(tr(ok), 'success'); fr.reload(); } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code === 'FRIENDS_LIMIT') showToast(tr('CL-FRIENDS-FULL', { n: friendsMax(clientConfig.value) }), 'error');
      else if (code === 'NOT_FOUND') { showToast(tr('CL-FRIENDS-REQ-GONE')); fr.reload(); } else app.handleError(e);
    } finally { setBusy(null); }
  };
  const d = fr.data;
  const invited = refs.data?.invited ?? [];
  const empty = !!d && !d.friends.length && !d.incoming.length && !d.outgoing.length && !invited.length;
  return (
    <>
      <ScreenTitle right={d ? <span class="small muted" data-testid="friends-count">{tr('CL-FRIENDS-COUNT', { n: d.friends.length, max: friendsMax(clientConfig.value) })}</span> : null}>{tr('CL-FRIENDS-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-FRIENDS">
        <div class="page col store-page">
          <div class="store-panel friends-panel col">
            <button class="btn accent invite-cta" data-testid="friends-call" disabled={!online.value || !link} onClick={() => { if (link) void shareInvite(app, link); }}>
              {assetEntry('ICO-INVITE') ? <Icon id="ICO-INVITE" fallback="" size={32} /> : <I n="party" size={32} />}
              <span class="col" style={{ gap: 2, alignItems: 'flex-start', flex: 1 }}>
                <span>{online.value ? tr('CL-FRIENDS-CALL') : tr('UI-OFFLINE')}</span>
                {reward ? <span class="invite-reward" data-testid="friends-call-reward">{tr('CL-FRIENDS-CALL-REWARD', { reward })}</span> : null}
              </span>
            </button>
            <button class="small invite-more" data-testid="friends-invite" onClick={() => app.nav.openSheet('MOD-INVITE')}>{tr('CL-FRIENDS-CALL-MORE')} ›</button>
            {fr.error && !fr.offline ? <ErrorBanner what={tr('CL-FRIENDS-TITLE')} onRetry={fr.reload} /> : null}
            {fr.offline ? <div class="banner" data-testid="friends-offline">{tr('CL-FRIENDS-OFFLINE')}</div> : null}
            {!d && fr.loading ? Array.from({ length: 6 }, (_, i) => <div key={i} class="skeleton" style={{ height: 52 }} />) : null}
            {empty ? (
              <div class="empty card" data-testid="friends-empty"><I n="navHome" size={32} class="ill" />{tr('CL-FRIENDS-EMPTY')}</div>
            ) : null}
            {d?.incoming.length ? (
              <section class="col" style={{ gap: 6 }} data-testid="friends-incoming">
                <b class="small">{tr('CL-FRIENDS-REQUESTS')}</b>
                {d.incoming.map((f) => (
                  <div key={f.publicId} class="list-row">
                    <span class="col" style={{ gap: 2, flex: 1 }}><b>{f.displayName}</b><span class="small muted">{tr('CL-FRIENDS-LVL', { n: f.level })}</span></span>
                    <button class="btn small outline" disabled={busy === f.publicId || !online.value} data-testid={`decline-${f.publicId}`} onClick={() => act(f.publicId, () => app.call('removeFriend', { params: { publicId: f.publicId } }), 'CL-FRIENDS-DECLINED')}>{tr('CL-FRIENDS-DECLINE')}</button>
                    <button class="btn small accent" disabled={busy === f.publicId || !online.value} data-testid={`accept-${f.publicId}`} onClick={() => act(f.publicId, () => app.call('acceptFriend', { params: { publicId: f.publicId }, body: {} }), 'CL-FRIENDS-ACCEPTED')}>{tr('CL-FRIENDS-ACCEPT')}</button>
                  </div>
                ))}
              </section>
            ) : null}
            {invited.length ? (
              <section class="col" style={{ gap: 6 }} data-testid="friends-invited">
                <span class="row" style={{ justifyContent: 'space-between' }}><b class="small">{tr('CL-REF-TITLE')}</b>{refs.data ? <span class="small muted" data-testid="ref-left">{refs.data.rewardsMonth ? tr('CL-REF-MONTH', { k: refs.data.rewardsMonth.used, n: refs.data.rewardsMonth.cap }) : tr('CL-REF-LEFT', { n: refs.data.rewardsLeft })}</span> : null}</span>
                {invited.map((i) => {
                  const st = invitedStatus(i);
                  return (
                    <div key={i.publicId} class="list-row" data-testid={`invited-${i.publicId}`}>
                      <span class="col" style={{ gap: 2, flex: 1 }}>
                        <b>{nameOf(i)}</b>
                        <span class="small muted">{st.progress !== undefined ? tr('CL-REF-PROGRESS', { n: st.progress }) : tr('CL-FRIENDS-LVL', { n: i.level })}{i.status === 'qualified_l3' ? ` · ${tr('CL-REF-NEXT')}` : ''}</span>
                      </span>
                      <span class={`small ${st.tone}`}>{tr(st.key)}</span>
                    </div>
                  );
                })}
              </section>
            ) : null}
            {d?.friends.length ? (
              <section class="col" style={{ gap: 6 }} data-testid="friends-list">
                <b class="small">{tr('CL-FRIENDS-TITLE')}</b>
                {d.friends.map((f) => (
                  <div key={f.publicId} class="list-row friend-row">
                    <button class="col friend-open" style={{ gap: 2, flex: 1, textAlign: 'left' }} data-testid={`friend-${f.publicId}`} onClick={() => app.nav.openSheet('MOD-PLAYER-CARD', { publicId: f.publicId })}>
                      <b>{f.displayName}</b>
                      <span class="small muted">{[tr('CL-FRIENDS-LVL', { n: f.level }), f.coopName, f.lastSeenAt ? seenAgo(f.lastSeenAt) : null].filter(Boolean).join(' · ')}</span>
                    </button>
                    <RespectButton publicId={f.publicId} given={!!f.respectedToday} testId={`respect-${f.publicId}`} />
                  </div>
                ))}
              </section>
            ) : null}
            {d?.outgoing.length ? (
              <section class="col" style={{ gap: 6 }} data-testid="friends-outgoing">
                <b class="small">{tr('CL-FRIENDS-OUTGOING')}</b>
                {d.outgoing.map((f) => (
                  <div key={f.publicId} class="list-row">
                    <span class="col" style={{ gap: 2, flex: 1 }}><b>{f.displayName}</b><span class="small muted">{tr('CL-FRIENDS-WAITS', { d: daysLeft(f.since) })}</span></span>
                    <button class="btn small outline" disabled={busy === f.publicId || !online.value} onClick={() => act(f.publicId, () => app.call('removeFriend', { params: { publicId: f.publicId } }), 'CL-FRIENDS-CANCELLED')}>{tr('CL-FRIENDS-CANCEL')}</button>
                  </div>
                ))}
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

/** «был 2 ч назад» (ux/10 §1): минуты, часы, дни; меньше 5 мин — «в сети». */
export function seenAgo(iso: string, now = Date.now()): string {
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (m < 5) return tr('CL-FRIENDS-ONLINE');
  if (m < 60) return tr('CL-FRIENDS-SEEN-MIN', { n: m });
  if (m < 48 * 60) return tr('CL-FRIENDS-SEEN-H', { n: Math.floor(m / 60) });
  return tr('CL-FRIENDS-SEEN-D', { n: Math.floor(m / 1440) });
}

/** Заявка живёт 7 дней (ГД 14 §2): сколько дней осталось. */
export function daysLeft(since: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((Date.parse(since) + 7 * 86_400_000 - now) / 86_400_000));
}

// ---------------- MOD-INVITE ----------------

/** «500 гаек и ключ» — состав награды словами (ux/10 §2: «{награда} — обоим»). */
export function grantText(g: { currencies?: Record<string, number> | undefined }): string {
  return Object.entries(g.currencies ?? {}).map(([k, v]) => amount(k, v)).join(tr('CL-AND'));
}

type ShareWa = { shareMessage?: (id: string, cb?: (sent: boolean) => void) => void };

/**
 * «Отправить в Telegram» (ux/10 §2): сервер готовит сообщение с картинкой и кнопкой «Играть» (POST /share/prepare,
 * Р-95), клиент открывает выбор чата shareMessage (Bot API 8.0). Нет 8.0 или сервер ответил 503 — t.me/share/url.
 */
export async function shareInvite(app: ReturnType<typeof useApp>, link: string): Promise<void> {
  app.haptic('medium');
  const wa = app.wa as (ShareWa & typeof app.wa) | null;
  if (wa?.shareMessage && versionAtLeast(wa, '8.0')) {
    try {
      const r = await app.call('prepareShare', { body: { kind: 'referral' } });
      wa.shareMessage(r.preparedMessageId, (sent) => { if (sent) showToast(tr('CL-INVITE-SENT'), 'success'); });
      return;
    } catch (e) {
      if (!(e instanceof ApiCallError) || (e.code !== 'UNAVAILABLE' && e.code !== 'NETWORK' && e.code !== 'TIMEOUT')) { app.handleError(e); return; }
    }
  }
  app.wa?.openTelegramLink?.(shareUrl(link, tr('CL-INVITE-SHARE-TEXT')));
}

/** Ссылка для «Поделиться» без подготовленного сообщения (ux/10 §2, запасной путь). */
export function shareUrl(link: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
}

export function InviteSheet({ entry }: SheetProps) {
  const app = useApp();
  const refs = useLoad(() => app.call('getReferrals'));
  const cached = storage.get('refLink');
  const link = refs.data?.link ?? cached ?? null;
  if (refs.data?.link && refs.data.link !== cached) storage.set('refLink', refs.data.link);
  const invited = refs.data?.invited ?? [];
  const rewarded = invited.filter((i) => i.status === 'qualified_l3' || i.status === 'qualified_l10').length;
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); showToast(tr('CL-INVITE-COPIED'), 'success'); } catch { showToast(link); }
  };
  const share = () => { if (link) void shareInvite(app, link); };
  const off = !online.value;
  return (
    <Sheet entry={entry} title={tr('CL-INVITE-TITLE')} actions={[
      { text: tr('CL-INVITE-COPY'), tone: 'outline', testId: 'invite-copy', disabled: !link, onClick: copy },
      { text: off ? tr('UI-OFFLINE') : tr('CL-INVITE-SHARE'), testId: 'invite-share', disabled: off || !link, onClick: () => share() },
    ]}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="MOD-INVITE-body">
        <I n="party" size={64} />
        <p style={{ margin: 0 }} data-testid="invite-body">{refs.data?.reward ? tr('CL-INVITE-BODY-REWARD', { reward: grantText(refs.data.reward) }) : tr('CL-INVITE-BODY')}</p>
        {link ? <code class="order-id invite-link" data-testid="invite-link">{link}</code>
          : refs.error ? <ErrorBanner what={tr('CL-INVITE-LINK')} onRetry={refs.reload} /> : <span class="skeleton" style={{ height: 20, width: '100%' }} />}
        {refs.data ? <span class="small muted" data-testid="invite-stats">{tr('CL-INVITE-STATS', { n: invited.length, k: rewarded })}</span> : null}
        <span class="small muted">{tr('CL-INVITE-NOTE')}</span>
      </div>
    </Sheet>
  );
}

// ---------------- MOD-MORE ----------------

/**
 * MOD-MORE (ux/03 §11): то, что не влезло в колонку «Ещё» на главном (`params.keys`), — только активные пункты;
 * без `keys` — все активные. Неактивных («скоро») нет вовсе. Внизу — версия и ID игрока.
 */
export function MoreSheet({ entry }: SheetProps) {
  const app = useApp();
  const fr = useLoad(() => app.call('getFriends'));
  const me = player.value;
  const keys = entry.params['keys'] as string[] | undefined;
  const items = useMoreItems().filter((it) => !keys || keys.includes(it.key));
  const badge = (it: MoreItem) => (it.key === 'CL-FRIENDS-TITLE' ? fr.data?.incoming.length ?? 0 : it.badge ?? 0);
  const copyId = async () => {
    if (!me) return;
    try { await navigator.clipboard.writeText(me.publicId); showToast(tr('CL-PURCH-COPIED'), 'success'); } catch { showToast(me.publicId); }
  };
  return (
    <Sheet entry={entry} title={tr('CL-MORE-TITLE')}>
      <div class="list more-list" data-testid="MOD-MORE-body">
        {items.map((it) => (
          <button key={it.key} class="list-row" data-testid={it.testId} onClick={() => { app.nav.closeSheets(); it.go(); }}>
            <MoreIcon item={it} size={22} />
            <span style={{ flex: 1 }}>{tr(it.key)}</span>
            {badge(it) ? <span class="badge" data-testid={`${it.testId}-badge`}>{badge(it)}</span> : null}
            <span class="accent-text">›</span>
          </button>
        ))}
      </div>
      <button class="small muted more-foot" data-testid="more-id" onClick={copyId}>{tr('CL-MORE-FOOT', { v: CLIENT_BUILD, id: me?.publicId ?? '—' })}</button>
    </Sheet>
  );
}
